import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { cleanStationName, STATION_NAME_MAX_LENGTH } from "score-common";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow } from "common-fe";
import { useStationMonitor } from "../hooks/useStationMonitor";
import StationMonitorBanners from "../components/StationMonitorBanners";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useCanAssignScribes } from "../hooks/useCanAssignScribes";
import { buildStationGroups } from "../util/stationMonitor";
import { buildScribeBlocks } from "../util/stationCard";
import StationCard from "../components/StationCard";
import SendBlockedDialog from "../components/SendBlockedDialog";

// A plain ticking clock, same pattern as ScribePresenceSection.jsx's useNow:
// a re-render trigger so elapsed-time cells advance on screen between
// fetches rather than freezing at whatever value was true on load.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const CLOCK_TICK_MS = 5000;

// #2827 (scribe-resilience workstream, docs/score/scribe-resilience.md gap
// G12 part B): the scoreadmin mirror of scribe-fe's own outbox (C18): the
// state of every station, so the tent can see every ring without walking to
// it. #3190 rebuilt it on GET /show/:showId/station-monitor: grouped under
// station headers (the name the tablet was given once at setup, #3508, with the
// last 4 characters of its id small beside it), one row per stretch of work (scribe name, Ring, Position, time), with
// the scribe's counts and reconcile status on that scribe's first row. The
// reconcile cell is read-only (#3268): reviewing a disagreement happens in the
// Review Queue. No id of any kind is rendered. Shaping lives in util/stationMonitor.js
// (unit tested); this component is render only.
export default function StationMonitor({ pollMs } = {}) {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.stationMonitor"));
  const { activeShowId, loading: showLoading } = useActiveShow();
  // #3508: test tablets are hidden unless this switch shows them.
  const [showTest, setShowTest] = useState(false);
  const {
    rows,
    visibleRows,
    testRows,
    olderRows,
    loading: routeLoading,
    error,
    clearStation,
    restoreStation,
    requestMachineClear,
    checkStationName,
    renameStation,
  } = useStationMonitor(activeShowId, { showTest, ...(pollMs === undefined ? {} : { pollMs }) });
  // #3548: Current and Older are two tabs (the old "Show older stations" switch is gone).
  const [tab, setTab] = useState("current");
  // The Send blocked dialog: the keys of the box that was pressed, so the open
  // dialog follows the next poll instead of freezing on the snapshot it opened with.
  const [blockedKeys, setBlockedKeys] = useState(null);
  const [actionError, setActionError] = useState(false);
  const canClear = useCanAssignScribes();
  // #3521: Clear station asks first (confirmClear holds the group being cleared),
  // and Clear and Restore both disable while their request runs, so a second press
  // cannot send a second POST. The ref is the synchronous guard (state lags a
  // render); pendingId is what the buttons render from.
  const [confirmClear, setConfirmClear] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const inFlight = useRef(false);
  const runStationAction = async (action, deviceId) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPendingId(deviceId);
    setActionError(false);
    try {
      await action(deviceId);
    } catch {
      setActionError(true);
    } finally {
      inFlight.current = false;
      setPendingId(null);
    }
  };
  // #3518: Clear The Machine, a different action from Clear station. It asks first
  // (confirmMachine holds the group), runs through the same one-at-a-time guard, and
  // says so when the station had already cleared (requested:false is not an error).
  const [confirmMachine, setConfirmMachine] = useState(null);
  const [machineAlreadyCleared, setMachineAlreadyCleared] = useState(null);
  const confirmAndClearMachine = async () => {
    if (!confirmMachine) return;
    const group = confirmMachine;
    await runStationAction(async (deviceId) => {
      const result = await requestMachineClear(deviceId);
      setMachineAlreadyCleared(result.requested ? null : group.key);
    }, group.key);
    setConfirmMachine(null);
  };
  const confirmAndClear = async () => {
    if (!confirmClear) return;
    await runStationAction(clearStation, confirmClear.key);
    setConfirmClear(null);
  };
  // #3520: Rename. `renaming` is the group being renamed, `renameValue` the box.
  // First press of Rename checks the 7-day same-name list; if anything matches
  // the warning shows and the button becomes "Rename anyway" (a warning, never a
  // block). `renameWarnedFor` remembers which name was warned about, so editing
  // the box again asks again.
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameMatches, setRenameMatches] = useState([]);
  const [renameWarnedFor, setRenameWarnedFor] = useState(null);
  const [renameError, setRenameError] = useState(false);
  const [renameBusy, setRenameBusy] = useState(false);
  const closeRename = () => {
    if (!renameBusy) setRenaming(null);
  };
  const openRename = (group) => {
    setRenaming(group);
    setRenameValue(group.stationName ?? "");
    setRenameMatches([]);
    setRenameWarnedFor(null);
    setRenameError(false);
  };
  const submitRename = async () => {
    const clean = cleanStationName(renameValue);
    if (!renaming || !clean || renameBusy) return;
    setRenameBusy(true);
    setRenameError(false);
    try {
      if (renameWarnedFor !== clean && clean !== renaming.stationName) {
        const matches = await checkStationName(clean, renaming.key).catch(() => []);
        if (matches.length > 0) {
          setRenameMatches(matches);
          setRenameWarnedFor(clean);
          return;
        }
      }
      await renameStation(renaming.key, clean);
      setRenaming(null);
    } catch {
      setRenameError(true);
    } finally {
      setRenameBusy(false);
    }
  };
  const renameWarningShown = renameWarnedFor !== null && renameWarnedFor === cleanStationName(renameValue);
  // #3190 (card 3187's empty-state gate): the skeleton shows while the show OR
  // the route is loading, so "No stations" can never flash before the data is in.
  const loading = showLoading || routeLoading;
  const now = useNow(CLOCK_TICK_MS);

  const currentGroups = buildStationGroups(visibleRows);
  const olderGroups = buildStationGroups(olderRows);
  const groups = tab === "older" ? olderGroups : currentGroups;
  // Nothing at all reported: the empty state. Current empty but older stations exist: the
  // none-recent state. Older empty: older.none.
  const blockedGroup = blockedKeys ? [...currentGroups, ...olderGroups].find((g) => g.key === blockedKeys.groupKey) : null;
  const blockedFor = blockedGroup
    ? { group: blockedGroup, block: buildScribeBlocks(blockedGroup.rows).find((b) => b.key === blockedKeys.blockKey) ?? null }
    : null;
  let emptyTestId = "station-monitor-empty";
  let emptyText = t("stationMonitor.noStations");
  if (tab === "older") {
    emptyTestId = "station-monitor-older-none";
    emptyText = t("stationMonitor.older.none");
  } else if (rows.length > 0) {
    emptyTestId = "station-monitor-none-recent";
    emptyText = t("stationMonitor.older.noneRecent");
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("stationMonitor.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("stationMonitor.subtitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="station-monitor-load-error">
          {t("stationMonitor.loadError")}
        </Alert>
      )}

      {/* #3427 stuck station, #3454 missing judge signature (and later siblings): one stack. */}
      {/* The banners count only visibleRows: a hidden or cleared station never warns. */}
      {!loading && <StationMonitorBanners showId={activeShowId} rows={visibleRows} />}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="station-monitor-action-error">
          {t("stationMonitor.clear.error")}
        </Alert>
      )}

      {!loading && testRows.length > 0 && (
        <Box sx={{ mb: 1 }} data-testid="station-monitor-test">
          <FormControlLabel
            control={<Switch checked={showTest} onChange={(e) => setShowTest(e.target.checked)} />}
            label={t("stationMonitor.test.switch", { count: testRows.length })}
          />
        </Box>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="current" label={t("stationMonitor.tabs.current", { count: currentGroups.length })} data-testid="station-monitor-tab-current" />
        <Tab value="older" label={t("stationMonitor.tabs.older", { count: olderGroups.length })} data-testid="station-monitor-tab-older" />
      </Tabs>

      {tab === "older" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="station-monitor-older-note">
          {t("stationMonitor.older.note")}
        </Typography>
      )}

      {loading ? (
        <Box data-testid="station-monitor-skeleton">
          <TableSkeleton rows={4} columns={7} />
        </Box>
      ) : groups.length === 0 ? (
        <Typography color="text.secondary" data-testid={emptyTestId}>
          {emptyText}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="station-monitor-cards">
          {groups.map((group) => (
            <StationCard
              key={group.key}
              group={group}
              now={now}
              t={t}
              canClear={canClear}
              pendingId={pendingId}
              onClear={(g) => setConfirmClear(g)}
              onClearMachine={(g) => {
                setMachineAlreadyCleared(null);
                setConfirmMachine(g);
              }}
              alreadyCleared={machineAlreadyCleared}
              onRename={openRename}
              onRestore={(deviceId) => runStationAction(restoreStation, deviceId)}
              onSeeBlocked={({ group: g, block }) => setBlockedKeys({ groupKey: g.key, blockKey: block.key })}
            />
          ))}
        </Box>
      )}

      <SendBlockedDialog target={blockedFor?.block ? blockedFor : null} onClose={() => setBlockedKeys(null)} t={t} />

      <Dialog
        open={confirmClear !== null}
        onClose={() => {
          if (pendingId === null) setConfirmClear(null);
        }}
        data-testid="station-monitor-clear-dialog"
      >
        <DialogTitle>{t("stationMonitor.clear.confirmTitle", { name: confirmClear?.stationName ?? t("stationMonitor.stationUnnamed") })}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" color="text.primary">
            <Typography gutterBottom>{t("stationMonitor.clear.confirmWhat")}</Typography>
            <Typography gutterBottom>{t("stationMonitor.clear.confirmNext")}</Typography>
            <Typography>{t("stationMonitor.clear.confirmNot")}</Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(null)} disabled={pendingId !== null} data-testid="station-monitor-clear-cancel">
            {t("stationMonitor.clear.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={confirmAndClear}
            disabled={pendingId !== null}
            startIcon={pendingId !== null ? <CircularProgress size={16} color="inherit" /> : null}
            data-testid="station-monitor-clear-confirm"
          >
            {t("stationMonitor.clear.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmMachine !== null}
        onClose={() => {
          if (pendingId === null) setConfirmMachine(null);
        }}
        data-testid="station-monitor-clear-machine-dialog"
      >
        <DialogTitle>{t("stationMonitor.clearMachine.confirmTitle", { name: confirmMachine?.stationName ?? t("stationMonitor.stationUnnamed") })}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" color="text.primary">
            <Typography gutterBottom>{t("stationMonitor.clearMachine.confirmWhat")}</Typography>
            <Typography gutterBottom>{t("stationMonitor.clearMachine.confirmSafe")}</Typography>
            <Typography>{t("stationMonitor.clearMachine.confirmNot")}</Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMachine(null)} disabled={pendingId !== null} data-testid="station-monitor-clear-machine-cancel">
            {t("stationMonitor.clearMachine.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmAndClearMachine}
            disabled={pendingId !== null}
            startIcon={pendingId !== null ? <CircularProgress size={16} color="inherit" /> : null}
            data-testid="station-monitor-clear-machine-confirm"
          >
            {t("stationMonitor.clearMachine.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renaming !== null} onClose={closeRename} fullWidth maxWidth="xs" data-testid="station-monitor-rename-dialog">
        <DialogTitle>
          {renaming && !renaming.stationName
            ? t("stationMonitor.nameStation.title")
            : t("stationMonitor.rename.title", { name: renaming?.stationName ?? "" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div" color="text.primary">
            <Typography gutterBottom>
              {renaming && !renaming.stationName ? t("stationMonitor.nameStation.intro") : t("stationMonitor.rename.intro")}
            </Typography>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t("stationMonitor.rename.label")}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitRename();
            }}
            inputProps={{ maxLength: STATION_NAME_MAX_LENGTH, "data-testid": "station-monitor-rename-input" }}
          />
          {renameWarningShown && (
            <Alert severity="warning" sx={{ mt: 1 }} data-testid="station-monitor-rename-warning">
              {t("stationMonitor.rename.warning", {
                count: renameMatches.length,
                ids: renameMatches.map((m) => `\u2026${m.idSuffix}`).join(", "),
              })}
            </Alert>
          )}
          {renameError && (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="station-monitor-rename-error">
              {t("stationMonitor.rename.error")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRename} disabled={renameBusy} data-testid="station-monitor-rename-cancel">
            {t("stationMonitor.rename.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={submitRename}
            disabled={renameBusy || !cleanStationName(renameValue) || cleanStationName(renameValue) === renaming?.stationName}
            startIcon={renameBusy ? <CircularProgress size={16} color="inherit" /> : null}
            data-testid="station-monitor-rename-save"
          >
            {renameWarningShown
              ? t("stationMonitor.rename.saveAnyway")
              : renaming && !renaming.stationName
                ? t("stationMonitor.nameStation.save")
                : t("stationMonitor.rename.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
